import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle2, TrendingUp, Users, Handshake, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import WhatsAppButton from '@/components/WhatsAppButton';
import { useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BecomeDistributor = () => {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    city: '',
    state: '',
    phone: '',
    email: '',
    monthly_requirement: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Become a Distributor – RSD Accessories Pvt. Ltd.';
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/distributor-enquiry`, formData);
      setSubmitted(true);
      toast.success('Enquiry submitted successfully! We\'ll contact you soon.');
      setFormData({
        name: '',
        company_name: '',
        city: '',
        state: '',
        phone: '',
        email: '',
        monthly_requirement: ''
      });
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'High-Profit Margins',
      description: 'Competitive pricing with attractive margins for sustainable growth'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Dedicated account manager and 24/7 customer support'
    },
    {
      icon: Handshake,
      title: 'Marketing Support',
      description: 'Co-branded marketing materials and promotional campaigns'
    },
    {
      icon: Globe,
      title: 'Pan-India Network',
      description: 'Join a growing network of distributors across India'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-black to-neutral-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-black mb-6">
            Partner with{' '}
            <span className="text-[#FF4D00]">RSD Accessories</span>
          </h1>
          <p className="text-xl md:text-2xl text-neutral-300 max-w-3xl mx-auto">
            Grow Your Business with a Trusted Indian Manufacturing Brand
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Partner With Us?</h2>
            <p className="text-lg text-neutral-600">Benefits of becoming an authorized distributor</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  data-testid={`benefit-${index}`}
                  className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF4D00] rounded-full mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-neutral-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Distributor Enquiry Form</h2>
            <p className="text-lg text-neutral-600">
              Fill out the form below and our team will get in touch with you within 24-48 hours
            </p>
          </div>

          {submitted && (
            <div data-testid="success-message" className="mb-8 bg-green-50 border border-green-200 rounded-xl p-6 flex items-center gap-4">
              <CheckCircle2 className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-green-900 mb-1">Thank you for your interest!</h3>
                <p className="text-green-700">We've received your enquiry and will contact you soon.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="distributor-form" className="bg-neutral-50 rounded-2xl p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <Label htmlFor="company_name" className="text-sm font-semibold mb-2 block">
                  Company Name *
                </Label>
                <Input
                  id="company_name"
                  name="company_name"
                  data-testid="company-input"
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="Enter company name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="city" className="text-sm font-semibold mb-2 block">
                  City *
                </Label>
                <Input
                  id="city"
                  name="city"
                  data-testid="city-input"
                  type="text"
                  required
                  value={formData.city}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="Enter city"
                />
              </div>

              <div>
                <Label htmlFor="state" className="text-sm font-semibold mb-2 block">
                  State *
                </Label>
                <Input
                  id="state"
                  name="state"
                  data-testid="state-input"
                  type="text"
                  required
                  value={formData.state}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="Enter state"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone" className="text-sm font-semibold mb-2 block">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  data-testid="phone-input"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-semibold mb-2 block">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  data-testid="email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="Enter email address"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="monthly_requirement" className="text-sm font-semibold mb-2 block">
                Expected Monthly Requirement *
              </Label>
              <Textarea
                id="monthly_requirement"
                name="monthly_requirement"
                data-testid="requirement-input"
                required
                value={formData.monthly_requirement}
                onChange={handleChange}
                className="min-h-24 rounded-lg"
                placeholder="Please describe your expected monthly order volume and product requirements"
              />
            </div>

            <Button
              type="submit"
              data-testid="submit-button"
              disabled={submitting}
              className="w-full bg-[#FF4D00] hover:bg-[#E04400] text-white rounded-full py-6 text-lg font-bold disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Enquiry'}
            </Button>
          </form>
        </div>
      </section>

      {/* Coverage Section */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Pan-India Coverage</h2>
          <p className="text-xl text-neutral-600 mb-8 max-w-2xl mx-auto">
            We are looking for distributors across all major cities and regions of India
          </p>
          <div className="inline-flex items-center gap-8 flex-wrap justify-center">
            {['Delhi NCR', 'Mumbai', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Ahmedabad'].map((city) => (
              <span key={city} className="text-lg font-semibold text-neutral-700">
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default BecomeDistributor;